import { OTP_LENGTH } from "@msuite/utils/strings";
import clsx from "clsx";
import { unstable_OneTimePasswordField as OneTimePasswordField } from "radix-ui";
import React from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { cn } from "../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";

export interface OTPFieldProps<T extends FieldValues>
	extends Omit<ExtendedInputWrapperProps<T>, "name"> {
	control: Control<T>;
	name: Path<T>;
	length?: number;
	divideIndex?: number[];
}

export function OTPField<T extends FieldValues>({
	control,
	isRequired,
	name,
	label,
	helperText,
	length = OTP_LENGTH,
	divideIndex = [],
	...rest
}: OTPFieldProps<T>) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, formState }) => {
				return (
					<InputWrapper
						label={label}
						name={field.name}
						helperText={helperText}
						isRequired={isRequired}
						errors={formState.errors}
						{...rest}
					>
						<OneTimePasswordField.Root
							className={cn(
								"grid w-full input p-0 h-[40px] has-focus:input-focused divide-x-[0.5px] overflow-hidden",
								"*:font-semibold *:font-mono *:uppercase *:text-xl",
							)}
							style={{
								gridTemplateColumns: `repeat(${length + divideIndex.length}, 1fr)`,
							}}
							validationType="alphanumeric"
							value={field.value}
							onValueChange={(value) => {
								field.onChange(value);
							}}
						>
							{Array.from({ length }).map((_, i) => (
								<React.Fragment key={i}>
									{divideIndex.includes(i) && (
										<div className="tabular-nums inline-flex text-center border-color flex-center">
											<p className="">-</p>
										</div>
									)}
									<OneTimePasswordField.Input
										key={i}
										className={clsx("inline-flex w-full text-center last:border-r-0 border-color")}
									/>
								</React.Fragment>
							))}
						</OneTimePasswordField.Root>
					</InputWrapper>
				);
			}}
		/>
	);
}
